import React, { useEffect, useRef, useState } from 'react'
import s from './Accordion.module.scss'
import cn from 'classnames'
import PlusIcon from 'stores/ooka_default/components/icons/PlusIcon'
import MinusIcon from 'stores/ooka_default/components/icons/Minus-icon'

interface RenderProps {
    isOpen: boolean
}

interface Content {
    id: string
    Title: ({ isOpen }: RenderProps) => React.ReactElement
    Content: ({ isOpen }: RenderProps) => React.ReactElement
    buttonStyles?: ({ isOpen }: RenderProps) => React.CSSProperties
    buttonClass?: ({ isOpen }: RenderProps) => string
    itemClassName?: string
    initialState?: boolean
}

interface Props {
    contents: Array<Content> | null
    OpenIcon?: React.ReactNode
    CloseIcon?: React.ReactNode
    className?: string
}

function AccordionItem({
    item,
    isOpen,
    onToggle,
    OpenIcon,
    CloseIcon
}: {
    item: Content
    isOpen: boolean
    onToggle: () => void
    OpenIcon: React.ReactNode
    CloseIcon: React.ReactNode
}) {
    const contentEl = React.useRef<HTMLDivElement>(null)
    const [height, setHeight] = useState<number | 'auto'>(0)

    useEffect(() => {
        if (isOpen && contentEl.current) {
            setHeight(contentEl.current.scrollHeight)
        } else {
            setHeight(0)
        }
    }, [isOpen])

    const { Title, Content } = item

    return (
        <div
            className={cn(
                s['accordion-item-container'],
                { 'pb-4': isOpen },
                item.itemClassName
            )}
        >
            <button
                onClick={onToggle}
                type="button"
                className={cn(s['button'], item.buttonClass?.({ isOpen }))}
                style={item.buttonStyles?.({ isOpen })}
            >
                <Title isOpen={isOpen} />
                {isOpen ? CloseIcon : OpenIcon}
            </button>
            <div
                style={{
                    height: isOpen ? `${height}px` : '0px',
                    transition: 'height 0.3s ease',
                    overflow: 'hidden'
                }}
                className={cn(s['content-container'], 'content-container')}
                role="contentinfo"
                ref={contentEl}
                aria-hidden={!isOpen}
            >
                <Content isOpen={isOpen} />
            </div>
        </div>
    )
}

function Accordion({
    contents,
    OpenIcon = <PlusIcon isAccordion />,
    CloseIcon = <MinusIcon isAccordion />,
    className
}: Props): React.ReactElement | null {
    const [openIndex, setOpenIndex] = React.useState<number | null>(null)
    const accordionContainerRef = useRef<HTMLDivElement>(null)

    if (!contents) return null

    const handleToggle = (index: number) => {
        setOpenIndex((prev) => {
            const isClosing = prev === index
            const isFirstOpen = prev === null
            const newIndex = isClosing ? null : index
            if (!isFirstOpen && !isClosing && accordionContainerRef.current) {
                accordionContainerRef.current.scrollIntoView({
                    behavior: 'smooth',
                    block: index < 3 ? 'start' : 'end'
                })
            }
            return newIndex
        })
    }

    return (
        <div
            className={cn(s['accordion-container'], className)}
            ref={accordionContainerRef}
        >
            {contents.map((content, index) => (
                <AccordionItem
                    key={content.id}
                    item={content}
                    OpenIcon={OpenIcon}
                    CloseIcon={CloseIcon}
                    isOpen={openIndex === index}
                    onToggle={() => handleToggle(index)}
                />
            ))}
        </div>
    )
}

export default Accordion
